import { PlusOutlined } from "@ant-design/icons";
import { Button, Select } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import type { MRQ } from "../../api-models";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { FilterToggle } from "../../components/FilterToggle";
import { ListPage } from "../../components/ListPage";
import { ProjectLink } from "../../components/ProjectLink";
import { SiteLabel } from "../../components/SiteLabel";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import { hasAnyGrant } from "../../helpers/permissions";

const STATUSES = [
  "DRAFT",
  "PENDING_APPROVAL",
  "REJECTED",
  "ALLOCATION_REQUIRED",
  "PARTIALLY_ALLOCATED",
  "FULLY_ALLOCATED",
  "PARTIALLY_DISPATCHED",
  "FULLY_DISPATCHED",
  "PARTIALLY_RECEIVED",
  "FULFILLED",
  "CANCELLED",
];

export default function RequestsListPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const canWrite = hasAnyGrant(user, "Requests.Write");

  return (
    <ListPage<MRQ>
      title="Material Requests"
      endpoint="/mrqs"
      searchPlaceholder="Search doc no or notes..."
      linkTo={(r) => `/requests/${r.id}`}
      actions={
        canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/requests/new")}
            data-tour="requests-new"
          >
            New request
          </Button>
        )
      }
      filters={(controls) => (
        <>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            options={STATUSES.map((s) => ({
              value: s,
              label: s.replaceAll("_", " "),
            }))}
            value={controls.value("status")}
            onChange={(v) => controls.set("status", v)}
          />
          {/* The dashboard's "open requests" and "overdue requests" tiles land here. */}
          <FilterToggle name="open" label="Open only" controls={controls} />
          <FilterToggle name="overdue" label="Overdue" controls={controls} />
        </>
      )}
      columns={[
        { title: "Doc No", dataIndex: "docNo", sorter: true },
        {
          title: "Project",
          render: (_, r) => <ProjectLink project={r.project} />,
        },
        { title: "Site", render: (_, r) => r.site?.name },
        {
          title: "Needed by",
          dataIndex: "neededBy",
          render: (v) => (v ? dayjs(v).format(DT_FORMATS.date) : ""),
          sorter: true,
        },
        {
          title: "Status",
          dataIndex: "status",
          sorter: true,
          render: (v) => <DocumentStatusTag status={v} />,
        },
      ]}
      renderCard={(r) => (
        <div>
          <strong>{r.docNo}</strong>
          <div>
            <SiteLabel site={r.site} project={r.project} />
          </div>
          <DocumentStatusTag status={r.status} />
        </div>
      )}
    />
  );
}
